import React, { useState, useEffect, useCallback } from 'react'
import {
  Plus,
  Edit2,
  Trash2,
  FileText,
  RefreshCw,
  AlertCircle,
  Filter,
  Copy,
  Check
} from 'lucide-react'
import clsx from 'clsx'
import * as adminApi from '../../services/adminApi'
import StatusBadge from '../../components/admin/StatusBadge'
import ConfirmDialog from '../../components/admin/ConfirmDialog'
import Toast, { ToastContainer } from '../../components/admin/Toast'
import InstructionEditModal from './components/InstructionEditModal'

// Instruction type configuration
const instructionTypes = {
  translation: { label: 'Translation', color: 'bg-blue-500' },
  translation_review: { label: 'Review', color: 'bg-purple-500' },
  translation_anthropic: { label: 'Translation (Anthropic)', color: 'bg-orange-500' },
  translation_gemini: { label: 'Translation (Gemini)', color: 'bg-green-500' },
  translation_test: { label: 'Translation (Test)', color: 'bg-gray-500' },
}

const InstructionsPage = () => {
  // State
  const [instructions, setInstructions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filterType, setFilterType] = useState('all')

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingInstruction, setEditingInstruction] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, instruction: null })
  const [isDeleting, setIsDeleting] = useState(false)

  // Toast state
  const [toasts, setToasts] = useState([])

  // Copy state
  const [copiedId, setCopiedId] = useState(null)

  // Add toast helper
  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, type }])
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Fetch instructions
  const fetchInstructions = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await adminApi.getInstructions()
      setInstructions(data)
    } catch (err) {
      console.error('Failed to fetch instructions:', err)
      setError(err.response?.data?.detail || 'Failed to load instructions')
      addToast('Failed to load instructions', 'error')
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    fetchInstructions()
  }, [fetchInstructions])

  // Filter instructions by type
  const filteredInstructions = instructions.filter(inst => {
    if (filterType === 'all') return true
    if (filterType === 'translation') return inst.type.startsWith('translation') && inst.type !== 'translation_review'
    if (filterType === 'review') return inst.type === 'translation_review'
    return inst.type === filterType
  })

  // Get unique types for filter
  const uniqueTypes = [...new Set(instructions.map(i => i.type))]

  // Open create modal
  const handleAddInstruction = () => {
    setEditingInstruction(null)
    setShowEditModal(true)
  }

  // Open edit modal
  const handleEditInstruction = (instruction) => {
    setEditingInstruction(instruction)
    setShowEditModal(true)
  }

  // Handle form submit
  const handleSubmit = async (data) => {
    try {
      if (editingInstruction) {
        await adminApi.updateInstruction(editingInstruction.id, data)
        addToast('Instruction updated successfully', 'success')
      } else {
        await adminApi.createInstruction(data)
        addToast('Instruction created successfully', 'success')
      }
      setShowEditModal(false)
      setEditingInstruction(null)
      fetchInstructions()
    } catch (err) {
      console.error('Failed to save instruction:', err)
      addToast(err.response?.data?.detail || 'Failed to save instruction', 'error')
      throw err
    }
  }

  // Confirm delete
  const handleDeleteClick = (instruction) => {
    setDeleteConfirm({ open: true, instruction })
  }

  // Execute delete
  const handleDeleteConfirm = async () => {
    const instruction = deleteConfirm.instruction
    if (!instruction) return

    try {
      setIsDeleting(true)
      await adminApi.deleteInstruction(instruction.id)
      addToast('Instruction deleted successfully', 'success')
      setDeleteConfirm({ open: false, instruction: null })
      fetchInstructions()
    } catch (err) {
      console.error('Failed to delete instruction:', err)
      addToast(err.response?.data?.detail || 'Failed to delete instruction', 'error')
    } finally {
      setIsDeleting(false)
    }
  }

  // Copy instruction text
  const handleCopy = async (instruction) => {
    try {
      await navigator.clipboard.writeText(instruction.instructions)
      setCopiedId(instruction.id)
      setTimeout(() => setCopiedId(null), 2000)
      addToast('Copied to clipboard', 'success')
    } catch (err) {
      addToast('Failed to copy', 'error')
    }
  }

  // Get type info
  const getTypeInfo = (type) => {
    return instructionTypes[type] || { label: type, color: 'bg-gray-500' }
  }

  // Truncate instruction preview
  const truncateText = (text, maxLength = 150) => {
    if (!text) return '-'
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            LLM Instructions
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage translation and review prompts for LLM models
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchInstructions}
            disabled={loading}
            className={clsx(
              'inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600',
              'rounded-md shadow-sm text-sm font-medium',
              'text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700',
              'hover:bg-gray-50 dark:hover:bg-gray-600',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <RefreshCw className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </button>
          <button
            onClick={handleAddInstruction}
            data-testid="add-instruction-button"
            className={clsx(
              'inline-flex items-center px-4 py-2',
              'border border-transparent rounded-md shadow-sm text-sm font-medium',
              'text-white bg-primary-600 hover:bg-primary-700',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
            )}
          >
            <Plus className="w-4 h-4 mr-2" />
            New Instruction
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center space-x-2">
        <Filter className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-500 dark:text-gray-400">Filter:</span>
        <div className="flex space-x-2">
          <button
            onClick={() => setFilterType('all')}
            className={clsx(
              'px-3 py-1 text-sm rounded-full',
              filterType === 'all'
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            )}
          >
            All ({instructions.length})
          </button>
          <button
            onClick={() => setFilterType('translation')}
            className={clsx(
              'px-3 py-1 text-sm rounded-full',
              filterType === 'translation'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            )}
          >
            Translation
          </button>
          <button
            onClick={() => setFilterType('review')}
            className={clsx(
              'px-3 py-1 text-sm rounded-full',
              filterType === 'review'
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            )}
          >
            Review
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Instructions table */}
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Instruction Preview
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Created
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading && instructions.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-4 py-8 text-center">
                  <RefreshCw className="w-6 h-6 mx-auto text-gray-400 animate-spin" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Loading instructions...
                  </p>
                </td>
              </tr>
            ) : filteredInstructions.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-4 py-8 text-center">
                  <FileText className="w-8 h-8 mx-auto text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {filterType === 'all' ? 'No instructions configured' : `No ${filterType} instructions found`}
                  </p>
                  <button
                    onClick={handleAddInstruction}
                    className="mt-3 text-sm text-primary-600 hover:text-primary-500"
                  >
                    Add your first instruction
                  </button>
                </td>
              </tr>
            ) : (
              filteredInstructions.map((instruction) => {
                const typeInfo = getTypeInfo(instruction.type)
                return (
                  <tr
                    key={instruction.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    data-testid="instruction-row"
                    data-instruction-id={instruction.id}
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <span className={clsx('w-2 h-2 rounded-full mr-2', typeInfo.color)} />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {typeInfo.label}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-4">
                        ID: {instruction.id}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap font-mono">
                        {truncateText(instruction.instructions)}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {formatDate(instruction.created_at)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleCopy(instruction)}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Copy instruction"
                        >
                          {copiedId === instruction.id ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEditInstruction(instruction)}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Edit instruction"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(instruction)}
                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          title="Delete instruction"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Available Variables Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
          Available Template Variables
        </h3>
        <div className="flex flex-wrap gap-2">
          {['{source_lang}', '{target_lang}', '{context}', '{batch_text}', '{pairs}'].map(variable => (
            <code
              key={variable}
              className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs"
            >
              {variable}
            </code>
          ))}
        </div>
        <p className="mt-2 text-xs text-blue-600 dark:text-blue-400">
          These variables are automatically replaced during translation. Use them in your instruction prompts.
        </p>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <InstructionEditModal
          instruction={editingInstruction}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowEditModal(false)
            setEditingInstruction(null)
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, instruction: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Instruction"
        message={`Are you sure you want to delete this ${deleteConfirm.instruction?.type} instruction? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Toasts */}
      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  )
}

export default InstructionsPage
